import React, {useCallback, useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useParams} from 'react-router-dom';

import {useAdminAccessRight} from 'src/hooks/access-rights';
import {TabContent} from 'src/modules/profile/account/tabs/admin-account-api-v2/components/tab-content';
import {
    deleteReleaseNote,
    getReleaseNotes,
    saveReleaseNote,
    updateReleaseNote,
} from 'src/openapi/admin/apis/ReleaseNotesControllerApi';
import {useAppDispatch} from 'src/store/hooks';

import {Group} from '@pb/kit-component-group';
import Typography from '@pb/kit-component-typography';
import {formatDate} from '@pb/kit-formatters';

import {ReleaseNotesView} from './release-notes.view';
import {clearError, setReleaseNotes} from './services/actions';

import type {ReleaseNote} from 'src/openapi/admin/models/ReleaseNote';
import type {AppState} from 'src/store/typedef';


export const ReleaseNotesController = () => {
    const params = useParams<{accountId: string}>();
    const accountId = params.accountId ? +params.accountId : -1;
    const {canEditApiV2} = useAdminAccessRight();
    const canEdit = canEditApiV2(accountId);
    const [loading, setLoading] = useState(false);

    const dispatch = useDispatch();
    const apiDispatch = useAppDispatch();

    const getNotes = useCallback(() => {

        apiDispatch(getReleaseNotes()).then(action => {
            if (action.payload.success) {
                dispatch(setReleaseNotes(action.payload.data));
                setLoading(false);
            }
        });
    }, [dispatch, apiDispatch]);

    useEffect(() => {
        getNotes();
    }, [getNotes]);

    const onCloseError = useCallback(() => {
        dispatch(clearError());
    }, [dispatch]);

    const onRemoveNote = useCallback((releaseNoteId: number) => {
        setLoading(true);

        apiDispatch(deleteReleaseNote({releaseNoteId})).then(action => {
            if (action.payload.success) {
                getNotes();
            } else {
                setLoading(false);
            }
        });
    }, [getNotes, apiDispatch]);

    const onSaveNote = useCallback((releaseNoteData: ReleaseNote) => {
        setLoading(true);

        apiDispatch(saveReleaseNote({releaseNote: releaseNoteData})).then(action => {
            if (action.payload.success) {
                getNotes();
            } else {
                setLoading(false);
            }
        });
    }, [getNotes, apiDispatch]);

    const onUpdateNote = useCallback((releaseNoteData: ReleaseNote) => {
        setLoading(true);

        apiDispatch(updateReleaseNote({releaseNote: {
            ...releaseNoteData,
            releaseDate: formatDate(new Date(releaseNoteData.releaseDate).toISOString(), 'YYYY-MM-DD'),
        }})).then(action => {
            if (action.payload.success) {
                getNotes();
            } else {
                setLoading(false);
            }
        });
    }, [getNotes, apiDispatch]);

    const error = useSelector((state: AppState) => state.apiv2.releaseNotes.error);
    const releaseNotes = useSelector((state: AppState) => state.apiv2.releaseNotes.data.releaseNotes);
    const changesInfo = useSelector((state: AppState) => state.apiv2.releaseNotes.data.changesInfo);

    return (
        <TabContent>
            <Group direction="v" gap="l">
                <Group direction="v">
                    <Typography
                        fontWeight="semi-bold"
                        variant="heading-level-3">
                        Release Notes
                    </Typography>
                    <ReleaseNotesView
                        releaseNotes={releaseNotes}
                        error={error}
                        onCloseError={onCloseError}
                        changesInfo={changesInfo}
                        onSaveNote={onSaveNote}
                        onRemoveNote={onRemoveNote}
                        onUpdateNote={onUpdateNote}
                        loading={loading}
                        canEdit={canEdit}
                    />
                </Group>
            </Group>
        </TabContent>
    );
};
